import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Tooltip, InputNumber } from "antd";
import styled, { css } from "styled-components";
import * as R from "ramda";

import * as gtag from "../../../../../lib/gtag";

import { Container, Title } from "./FilterComponents";

const Row = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  .div {
    margin: 0 15px;
  }
  input {
    max-width: 90px;
  }
  .clear {
    margin-left: 12px;
    min-width: 32px;
    height: 32px;
  }
`;

const errorMixin = css`
  border-color: ${({ theme }) => theme["@alert"]} !important;
  :focus,
  :hover {
    box-shadow: 0 0 0 2px ${({ theme }) => theme["@alert"]}22 !important;
  }
`;

const StyledInput = styled(InputNumber)`
  ${({ error }) => (error ? errorMixin : "")}
`;

const ErrorLabel = styled.p`
  color: ${({ theme }) => theme["@alert"]};
`;

const SalaryFilter = ({ loading, onChange, value }) => {
  const { t } = useTranslation();
  const [internalValue, setInternalValue] = useState(value || [0, null]);
  const [error, setError] = useState(false);
  const [min, max] = internalValue;

  const handleMinChange = useCallback(
    newValue => {
      setInternalValue([newValue === "" ? 0 : Number(newValue), max]);
      if (max !== null && newValue > max)
        setError(t("opportunity:candidates.salaryFilter.validationError"));
      else if (error) setError(false);
    },
    [error, max, t]
  );

  const handleMaxChange = useCallback(
    newValue => {
      setInternalValue([min, newValue === "" ? null : Number(newValue)]);
      if (newValue !== "" && newValue < min)
        setError(t("opportunity:candidates.salaryFilter.validationError"));
      else if (error) setError(false);
    },
    [error, min, t]
  );

  const handleReset = useCallback(() => {
    setInternalValue([0, null]);
    onChange([0, null]);
  }, [onChange]);

  const handleSubmit = useCallback(() => {
    gtag.event({
      category: "VacancyCandidates",
      action: `filterSalary`
    });
    onChange([min, max]);
  }, [max, min, onChange]);

  const canSubmit = useMemo(() => !(min === null || error), [error, min]);

  const shouldShowResetButton = useMemo(() => {
    const [minValue, maxValue] = value;
    return minValue === min && max !== null && maxValue === max;
  }, [max, min, value]);

  const handleKeyPress = useCallback(
    event => {
      if (event.key === "Enter") {
        if (canSubmit) handleSubmit();
        else if (!error && max !== null && min > max)
          setError(t("opportunity:candidates.salaryFilter.validationError"));
      }
    },
    [canSubmit, error, handleSubmit, max, min, t]
  );

  return (
    <details open={value && !R.isEmpty(value) && !R.equals(value, [0, null])}>
      <Title>
        <h4>{t("opportunity:candidates.salaryFilter.title")}</h4>
      </Title>
      <Container>
        <Row>
          <Tooltip
            title={
              min > 999 && `$ ${min || 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
            }
          >
            <StyledInput
              onKeyPress={handleKeyPress}
              error={error}
              min={0}
              disabled={loading}
              onChange={handleMinChange}
              placeholder={t("opportunity:screening.shortMin")}
              type="number"
              value={min}
            />
          </Tooltip>
          <span className="div">-</span>
          <Tooltip
            title={
              max > 999 && `$ ${max || 0}`.replace(/\B(?=(\d{3})+(?!\d))/g, ".")
            }
          >
            <StyledInput
              onKeyPress={handleKeyPress}
              error={error}
              min={0}
              disabled={loading}
              onChange={handleMaxChange}
              placeholder={t("opportunity:screening.shortMax")}
              type="number"
              value={max}
            />
          </Tooltip>
          <Button
            className="clear"
            shape="circle"
            icon={shouldShowResetButton ? "close" : "arrow-right"}
            type="primary"
            disabled={!canSubmit}
            onClick={shouldShowResetButton ? handleReset : handleSubmit}
          />
        </Row>
        {error && <ErrorLabel>{error}</ErrorLabel>}
      </Container>
    </details>
  );
};

export default SalaryFilter;
